import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  DataTable,
  buildListMasterTableColumns,
} from "../../../components/data-table";
import { ListMasterPageLayout } from "../../../components/list-master";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  zonesAPI,
  statesAPI,
  type Zone,
  type State,
  type StateCreatePayload,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

function getZoneName(state: State, zones: Zone[]) {
  if (state.zone_name) {
    return state.zone_name;
  }
  const id = state.zone;
  if (id === null) {
    return "—";
  }
  const z = zones.find(p => String(p.id) === String(id));
  return z?.name ?? String(id);
}

export default function StatePage() {
  const [zones, setZones] = useState<Zone[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<{
    name: string;
    zone: string;
    is_active: boolean;
  }>({
    name: "",
    zone: "",
    is_active: true,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<State | null>(null);
  const [deleting, setDeleting] = useState<State | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchZones = async () => {
    try {
      const list = await zonesAPI.list();
      setZones(Array.isArray(list) ? list : []);
    } catch {
      setZones([]);
    }
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await statesAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setStates(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load states");
      setStates([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    fetchZones();
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("State name is required");
      return;
    }
    if (!form.zone) {
      toast.error("Please select a zone");
      return;
    }
    setSubmitLoading(true);
    try {
      const payload: StateCreatePayload = {
        name: form.name.trim(),
        zone: Number(form.zone) || form.zone,
        is_active: form.is_active,
      };
      if (editing) {
        await statesAPI.update(editing.id, payload);
        toast.success("State updated successfully");
      } else {
        await statesAPI.create(payload);
        toast.success("State created successfully");
      }
      setDialogOpen(false);
      setForm({ name: "", zone: "", is_active: true });
      setEditing(null);
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to save state";
      toast.error(String(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const openEdit = useCallback(
    (s: State) => {
      setEditing(s);
      setForm({
        name: s.name,
        zone:
          s.zone !== null
            ? String(s.zone)
            : zones[0]?.id !== null
              ? String(zones[0].id)
              : "",
        is_active: s.is_active ?? true,
      });
      setDialogOpen(true);
    },
    [zones]
  );

  const tableColumns = useMemo(
    () =>
      buildListMasterTableColumns<State>({
        currentPage,
        pageSize,
        onEdit: openEdit,
        onDelete: setDeleting,
        showCode: false,
        extraColumns: [
          {
            id: "zone",
            label: "Zone",
            width: 160,
            renderCell: row => getZoneName(row, zones),
          },
        ],
      }),
    [currentPage, pageSize, openEdit, zones]
  );

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeleteLoading(true);
    try {
      await statesAPI.delete(deleting.id);
      toast.success("State deleted successfully");
      setDeleting(null);
      if (states.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      const msg = (err as Error)?.message ?? "Failed to delete state";
      toast.error(String(msg));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">State</h1>
        <p className="text-muted-foreground">Manage states</p>
      </div>
      <ListMasterPageLayout
        tableId="list-master-state"
        tableColumns={tableColumns}
        listTitle={`States (${totalItems})`}
        searchPlaceholder="Search states..."
        searchTerm={searchTerm}
        onSearchChange={v => {
          setSearchTerm(v);
          setCurrentPage(1);
        }}
        headerActions={
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                setForm({ name: "", zone: "", is_active: true });
                setEditing(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setForm({
                    name: "",
                    zone: zones[0]?.id !== null ? String(zones[0].id) : "",
                    is_active: true,
                  });
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit State" : "Create New State"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Zone *</Label>
                  <Select
                    value={String(form.zone ?? "")}
                    onValueChange={v => setForm(prev => ({ ...prev, zone: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {zones.map(z => (
                        <SelectItem key={z.id} value={String(z.id)}>
                          {z.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="state-name">Name *</Label>
                  <Input
                    id="state-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="State name"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="state-active"
                    checked={form.is_active ?? true}
                    onCheckedChange={c =>
                      setForm(prev => ({ ...prev, is_active: c }))
                    }
                  />
                  <Label htmlFor="state-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={submitLoading}>
                    {submitLoading ? (
                      <CustomLoader size="xs" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <CustomLoader size="md" />
          </div>
        ) : (
          <DataTable
            embedded
            hideToolbar
            tableId="list-master-state"
            columns={tableColumns}
            data={states}
            getRowKey={s => s.id}
            emptyMessage={
              totalItems === 0 && !searchTerm.trim()
                ? "No states yet. Create one to get started."
                : "No states found matching your search."
            }
          />
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <CommonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </ListMasterPageLayout>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete State</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
